import { Software } from '@/lib/types/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area'; 

function parseDescription(description: string): string {
  return description
    // Remove citation links
    .replace(/\(Citation:[^)]+\)/g, '')
    // Remove MITRE ATT&CK links while keeping the text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    // Remove campaign links
    .replace(/\[[^\]]+\]\([^)]+\/campaigns\/[^)]+\)/g, '')
    // Remove sup tags and their content
    .replace(/<sup>[^<]+<\/sup>/g, '')
    // Clean up any double spaces
    .replace(/\s+/g, ' ')
    // Clean up any trailing/leading spaces
    .trim();
}

function formatDescription(description: string): JSX.Element {
  const parts = description.split(/(<code>[^<]+<\/code>)/g);
  
  return (
    <>
      {parts.map((part, index) => {
        if (part.startsWith('<code>') && part.endsWith('</code>')) {
          // Extract code content
          const code = part.slice(6, -7);
          return (
            <code
              key={index}
              className="relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm font-semibold"
            >
              {code}
            </code>
          );
        }
        return <span key={index}>{parseDescription(part)}</span>;
      })}
    </>
  );
}

interface SoftwareListProps {
  software: Software[];
  title?: string;
}

export function SoftwareList({ software, title = 'Associated Software' }: SoftwareListProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[400px] pr-4">
          <div className="space-y-6">
            {software.map((sw) => (
              <div key={sw.software_id} className="space-y-2">
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{sw.software_id}</Badge>
                  {sw.link ? (
                    <a
                      href={sw.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-lg font-semibold hover:underline"
                    >
                      {parseDescription(sw.name)}
                    </a>
                  ) : (
                    <h3 className="text-lg font-semibold">
                      {parseDescription(sw.name)}
                    </h3>
                  )}
                </div>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {formatDescription(sw.description)}
                </p>
              </div>
            ))}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}